import { useMemo } from 'react';
import uniq from 'lodash/uniq';
import { isNumber } from 'utility/utility';
import { useBrands, useProjects, useTeams, useUsers } from '@round/creatorbase-helpers/api-hooks';
import { ProjectSelectTableRow } from './ProjectSelectTable/helpers';

type Params = { projectIds: number[]; enabled: boolean };

const sortedIdParam = (ids: number[]) => [...ids].sort((a, b) => a - b).join(',');

export function useInitialProjects({ projectIds, enabled }: Params) {
    const wantsFetch = enabled && projectIds.length > 0;

    const projectsQ = useProjects(
        { id: sortedIdParam(projectIds), page_size: projectIds.length },
        { enabled: wantsFetch }
    );
    const projects = useMemo(() => projectsQ.data?.results ?? [], [projectsQ.data]);

    const teamIds = useMemo(() => uniq(projects.map((p) => p.team_id)).filter(isNumber), [projects]);
    const brandIds = useMemo(() => uniq(projects.map((p) => p.brand_id)).filter(isNumber), [projects]);
    const userIds = useMemo(() => uniq(projects.flatMap((p) => p.users_assigned_to_campaigns)), [projects]);

    const teamsQ = useTeams({ id: sortedIdParam(teamIds), page_size: teamIds.length }, { enabled: teamIds.length > 0 });
    const brandsQ = useBrands(
        { id: sortedIdParam(brandIds), page_size: brandIds.length },
        { enabled: brandIds.length > 0 }
    );
    const usersQ = useUsers({ id: sortedIdParam(userIds), page_size: userIds.length }, { enabled: userIds.length > 0 });

    const hydrated: ProjectSelectTableRow[] = useMemo(
        () =>
            projects.map((project) => ({
                ...project,
                team: teamsQ.data?.results.find((t) => t.id === project.team_id) ?? null,
                brand: brandsQ.data?.results.find((b) => b.id === project.brand_id) ?? null,
                users: project.users_assigned_to_campaigns
                    .map((uid) => usersQ.data?.results.find((u) => u.id === uid) ?? null)
                    .filter((u): u is NonNullable<typeof u> => !!u),
            })),
        [projects, teamsQ.data, brandsQ.data, usersQ.data]
    );

    const isLoading = projectsQ.isLoading || teamsQ.isLoading || brandsQ.isLoading || usersQ.isLoading;
    const isError = !!projectsQ.errorMessage;

    return { data: enabled ? hydrated : [], isLoading, isError };
}
